import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import { Dashboard, useUppy } from '@uppy/react';
import { dataProvider } from 'providers/dataProvider';
import { Uppy } from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/file-input/dist/style.css';
import { makeStyles } from '@material-ui/core/styles';
import GeoStatusAlert from 'components/GeoStatusAlert';
import ImageViewer from './master-sheet-image-viewer';
import { PhaseTypeEnum } from '@vatos-pas/common';
import type { PhaseApprovalOptionValues } from '../../types/phaseOptions';
import {
  PhaseOptionsSection,
  paintOptionControls,
} from './PhaseOptionsSection';
import { useTrimOptionSelection } from './useTrimOptionSelection';
import { TrimTwoStandingWarning } from './TrimOptionAlerts';

const useStyles = makeStyles({
  img: {
    marginTop: '10px',
    width: '80px',
    marginRight: '10px',
    cursor: 'pointer',
  },
  dialog: {
    height: '90vh',
  },
  uploader: {
    height: '30vh',
  },
  cancelButton: {
    color: '#e6005a',
  },
  photoDialog: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0px 24px 18px 24px',
  },
});

const getJobPhasePhotos = async (dataProvider, jobPhaseId, setPhasePhotos) => {
  const jobPhasePhotosResult = await dataProvider.getList('job-phase-photo', {
    filter: {
      'jobPhaseId||$eq||': jobPhaseId,
    },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'createdAt', order: 'DESC' },
  });
  setPhasePhotos(jobPhasePhotosResult.data);
};

const createJobPhasePhoto = async (
  dataProvider,
  fileName,
  jobPhaseId,
  setPhasePhotos,
) => {
  const jobPhasePhotoPayload = {
    fileName,
    jobPhaseId,
  };
  void dataProvider.create('/job-phase-photo', {
    data: jobPhasePhotoPayload,
  });

  setTimeout(() => {
    getJobPhasePhotos(dataProvider, jobPhaseId, setPhasePhotos);
  }, 200);
};

interface PhotoUploadDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedPhase: {
    id: string;
    phaseType: PhaseTypeEnum;
    trimOptionSelected?: boolean | null;
  };
  isViewPhotos: boolean;
  job: any;
  approveJob?: (options: PhaseApprovalOptionValues) => void;
  geoCoords?: [number, number] | null;
  geoAccuracy?: number | null;
  geoLoading?: boolean;
  requestLocation?: () => void;
  geoError?: string;
}

export const PhotoUploadDialog: React.FC<PhotoUploadDialogProps> = props => {
  const classes = useStyles();
  const [imageViewerOpen, setImageViewerOpen] = useState<boolean>(false);
  const [phasePhotos, setPhasePhotos] = useState<any>([]);
  const [imageSelected, setImageSelected] = useState<any>();

  const [exteriorOptionsCompleted, setExteriorOptionsCompleted] =
    useState<boolean>(false);
  const [interiorOptionsCompleted, setInteriorOptionsCompleted] =
    useState<boolean>(false);
  const trim = useTrimOptionSelection({
    open: props.open,
    phase: props.selectedPhase,
    job: props.job?.job,
  });

  React.useEffect(() => {
    if (props.job?.job) {
      setExteriorOptionsCompleted(
        props.job.job.exteriorOptionsFinished || false,
      );
      setInteriorOptionsCompleted(
        props.job.job.interiorOptionsFinished || false,
      );
    }
  }, [props.job]);

  const isPaintPhaseWithOptions =
    props.selectedPhase?.phaseType === PhaseTypeEnum.FinalPaint ||
    props.selectedPhase?.phaseType === PhaseTypeEnum.InteriorPaint;

  const isFinalPaint =
    props.selectedPhase?.phaseType === PhaseTypeEnum.FinalPaint;
  const isInteriorPaint =
    props.selectedPhase?.phaseType === PhaseTypeEnum.InteriorPaint;

  const onBeforeUpload = files => {
    Object.keys(files).forEach((key: any) => {
      files[key].fileName = `job-phase-id-${
        props.selectedPhase.id
      }-${new Date().getTime()}-${files[key].name}`;
    });
    return files;
  };

  const uppy = useUppy(() => {
    return new Uppy({
      autoProceed: true,
      onBeforeUpload,
      restrictions: {
        allowedFileTypes: ['image/*', '.jpg', '.jpeg', '.png', '.gif'],
      },
    }).use(AwsS3, {
      getUploadParameters(file: any) {
        return dataProvider
          .create('/uploads/sign', {
            data: {
              contentType: file.type,
              objectName: `${file.fileName}`,
            },
          })
          .then(({ data }) => {
            return {
              method: 'PUT',
              url: data.signedUrl,
              fields: data.fields,
              headers: {
                'Content-Type': file.type,
              },
            };
          });
      },
    });
  });

  useEffect(() => {
    if (props.open && props.selectedPhase && props.selectedPhase.id) {
      getJobPhasePhotos(dataProvider, props.selectedPhase.id, setPhasePhotos);
    } else if (!props.open) {
      setPhasePhotos([]);
    }
  }, [props.open, props.selectedPhase]);

  useEffect(() => {
    const handleComplete = _response => {
      _response.successful.forEach((itemResponse: any) => {
        createJobPhasePhoto(
          dataProvider,
          itemResponse.fileName,
          props.selectedPhase.id,
          setPhasePhotos,
        );
      });
      uppy.reset();
    };

    uppy.on('complete', handleComplete);

    return () => {
      uppy.off('complete', handleComplete);
    };
  }, [props.selectedPhase?.id]);

  useEffect(() => {
    if (props.open && !props.isViewPhotos && props.requestLocation) {
      props.requestLocation();
    }
  }, [props.open, props.isViewPhotos, props.requestLocation]);

  const getApproveOptions = (): PhaseApprovalOptionValues => {
    return {
      trimOptionSelected: trim.selected,
      interiorOptionsCompleted: props.job?.job?.interiorOptionsFinished
        ? true
        : interiorOptionsCompleted,
      exteriorOptionsCompleted: props.job?.job?.exteriorOptionsFinished
        ? true
        : exteriorOptionsCompleted,
    };
  };

  const handleApproveClick = () => {
    if (props.approveJob) {
      props.approveJob(getApproveOptions());
    }
  };

  const geoAlertContent = !props.isViewPhotos ? (
    <GeoStatusAlert
      loading={props.geoLoading ?? false}
      error={props.geoError}
      coords={props.geoCoords}
      action="approving"
    />
  ) : undefined;

  const paintOptions = isPaintPhaseWithOptions
    ? paintOptionControls(props.job?.job, {
        exterior: {
          checked: exteriorOptionsCompleted,
          onChange: setExteriorOptionsCompleted,
          disabled:
            (isFinalPaint && props.job?.job?.exteriorOptionsFinished) ||
            (isInteriorPaint &&
              props.job?.job?.exteriorOptionsFinished &&
              props.job?.job?.exteriorOptionsFinishedJobPhaseId !==
                props.selectedPhase?.id),
        },
        interior: {
          checked: interiorOptionsCompleted,
          onChange: setInteriorOptionsCompleted,
          disabled:
            (isFinalPaint && props.job?.job?.interiorOptionsFinished) ||
            (isInteriorPaint &&
              props.job?.job?.interiorOptionsFinished &&
              props.job?.job?.interiorOptionsFinishedJobPhaseId !==
                props.selectedPhase?.id),
        },
      })
    : [];

  const approvalOptions = trim.control
    ? [...paintOptions, trim.control]
    : paintOptions;

  const showApprovalOptions = !props.isViewPhotos && approvalOptions.length > 0;

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="alert-dialog-title-"
      aria-describedby="alert-dialog-description9"
    >
      <DialogTitle id="alert-dialog-ti9tle">
        {props.isViewPhotos ? 'View Job Phase Photos' : 'Photos'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-desc9ription">
          {!props.isViewPhotos && (
            <Dashboard
              disableThumbnailGenerator
              showLinkToFileUploadResult={false}
              uppy={uppy}
              height={100}
            />
          )}
        </DialogContentText>
        <Box>
          <Box>
            {phasePhotos.map((phasePhoto: any) => {
              return (
                <img
                  className={classes.img}
                  onClick={() => {
                    setImageSelected(phasePhoto);
                    setImageViewerOpen(true);
                  }}
                  src={phasePhoto.fileUrl}
                  key={phasePhoto.id}
                />
              );
            })}
          </Box>
        </Box>

        {showApprovalOptions && (
          <PhaseOptionsSection
            divided
            prompt={
              trim.show
                ? 'Confirm whether the contractor has completed the selected option during this phase.'
                : 'Confirm whether the contractor painted the selected interior and/or exterior options during this phase.'
            }
            options={approvalOptions}
            supplementalContent={
              trim.isTrimTwo ? <TrimTwoStandingWarning /> : undefined
            }
          />
        )}

        {!props.isViewPhotos && geoAlertContent && (
          <Box mt={2}>{geoAlertContent}</Box>
        )}
      </DialogContent>
      <DialogActions className={classes.photoDialog}>
        <Button
          onClick={props.handleClose}
          color="primary"
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Box display="grid" gridAutoFlow="column" gridGap={30}>
          {!props.isViewPhotos && (
            <Button
              onClick={handleApproveClick}
              color="primary"
              autoFocus
              variant="contained"
              disabled={trim.isTrimTwo && !trim.selected}
            >
              Approve Phase
            </Button>
          )}
        </Box>
      </DialogActions>
      <ImageViewer
        imageSelected={imageSelected}
        open={imageViewerOpen}
        isViewPhotos={props.isViewPhotos}
        handleClose={() => setImageViewerOpen(false)}
        getJobPhasePhotos={() =>
          getJobPhasePhotos(
            dataProvider,
            props.selectedPhase.id,
            setPhasePhotos,
          )
        }
      />
    </Dialog>
  );
};

export default PhotoUploadDialog;
